import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { formatDisplayUserId } from "../utils/userIdFormat";
import { useResetUserPasswordMutation } from "../services/auth/authApi";
import { getDomainAppUrl } from "../utils/domainConfig";

const CloseIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.05-.06-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z"></path>
  </svg>
);

const SUBDOMAIN_MAP = {
  Client:     '',
  Agent:      'admin',
  Super:      'admin',
  Master:     'admin',
  MiniAdmin:  'admin',
  SuperAdmin: 'admin',
  Admin:      'admin',
};

const getLoginUrl = (userType, loginDomain) => {
  const baseDomain = loginDomain
    ? loginDomain.replace(/^https?:\/\//, '')
    : getDomainAppUrl();
  const sub = SUBDOMAIN_MAP[userType];
  if (!sub) return `https://${baseDomain}`;
  return `https://${sub}.${baseDomain}`;
};

const UserCreatedModal = ({ isOpen, onClose, userId, password, userType, isClient, loginDomain }) => {
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState(password);
  const [resetUserPassword, { isLoading: isResetting }] = useResetUserPasswordMutation();

  useEffect(() => {
    setCurrentPassword(password);
  }, [password]);

  if (!isOpen) return null;

  const handleUpdatePassword = async () => {
    if (!newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }
    try {
      await resetUserPassword({ userId, newPassword }).unwrap();
      setCurrentPassword(newPassword);
      setNewPassword("");
      setShowPasswordInput(false);
      toast.success("Password updated successfully!");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update password");
    }
  };

  const loginUrl = userType
    ? getLoginUrl(userType, loginDomain)
    : isClient
      ? `https://${getDomainAppUrl()}`
      : `https://admin.${getDomainAppUrl()}`;

  const shareMessage = `🎉 Registration Successful!
🧑 Username: ${userId}
🔑 Password: ${currentPassword}
👉 Login here: ${loginUrl}
Welcome to our platform! 🚀`;

  const handleWhatsAppShare = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank');
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(shareMessage)
      .then(() => toast.success("User information copied to clipboard!"))
      .catch(() => toast.error("Failed to copy to clipboard"));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50"></div>
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        style={{
          boxShadow:
            "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors z-10"
        >
          <CloseIcon />
        </button>

        <div className="bg-[#0F2327] px-6 py-4 rounded-t-lg">
          <h3 className="text-lg font-semibold text-white text-center">
            🎉 User Created Successfully!
          </h3>
        </div>

        <div className="px-6 py-6">
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-600 text-sm">Username:</span>
                <span className="font-semibold text-lg">{formatDisplayUserId(userId)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">Password:</span>
                <span className="font-semibold text-lg">{currentPassword}</span>
              </div>
            </div>

            {showPasswordInput ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="flex-1 border border-gray-300 px-3 py-2 focus:outline-none focus:border-blue-500"
                  disabled={isResetting}
                />
                <button
                  onClick={handleUpdatePassword}
                  disabled={isResetting}
                  className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-blue-300 transition-colors"
                  style={{ borderRadius: 0 }}
                >
                  {isResetting ? "Updating..." : "Save"}
                </button>
                <button
                  onClick={() => { setShowPasswordInput(false); setNewPassword(""); }}
                  disabled={isResetting}
                  className="px-4 py-2 bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                  style={{ borderRadius: 0 }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowPasswordInput(true)}
                className="w-full px-4 py-2 bg-gray-600 text-white hover:bg-gray-700 transition-colors"
                style={{ borderRadius: 0 }}
              >
                Update Password
              </button>
            )}

            <div className="text-center text-sm text-gray-600">
              <p>
                Login at:{" "}
                <a
                  href={loginUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  {loginUrl.replace("https://", "")}
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={handleWhatsAppShare}
            className="flex-1 px-4 py-2 bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
            style={{ borderRadius: 0 }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </button>
          <button
            onClick={handleCopy}
            className="flex-1 px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 transition-colors"
            style={{ borderRadius: 0 }}
          >
            Copy
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserCreatedModal;
